import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PhygitalIcon from "@/components/PhygitalIcon";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Server,
  Database,
  Globe,
  Smartphone,
  Zap,
  RefreshCw
} from "lucide-react";

const StatusPage = () => {
  const [status, setStatus] = useState({
    api: { status: "checking", latency: null },
    database: { status: "checking" },
    frontend: { status: "operational" }
  });
  const [lastChecked, setLastChecked] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
      const data = await response.json();
      const latency = Date.now() - startTime;
      
      setStatus({
        api: { 
          status: response.ok ? "operational" : "degraded",
          latency 
        },
        database: { 
          status: data.database === "healthy" ? "operational" : "degraded"
        },
        frontend: { status: "operational" }
      });
    } catch (error) {
      setStatus({
        api: { status: "outage", latency: null },
        database: { status: "unknown" },
        frontend: { status: "operational" }
      });
    }
    
    setLastChecked(new Date());
    setLoading(false);
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "degraded":
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "outage":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500 animate-spin" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "operational":
        return "bg-green-100 text-green-700";
      case "degraded":
        return "bg-amber-100 text-amber-700";
      case "outage":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const allOperational = Object.values(status).every(s => s.status === "operational");

  // Development roadmap items
  const roadmap = [
    { feature: "Provider Dashboard", status: "complete", date: "Jan 2026" },
    { feature: "Client Check-in Flow", status: "complete", date: "Jan 2026" },
    { feature: "Services CRUD", status: "complete", date: "Jan 2026" },
    { feature: "Manual Appointments", status: "complete", date: "Jan 2026" },
    { feature: "Appointment Chat/Edits", status: "in-progress", date: "Jan 2026" },
    { feature: "Experiences Architecture", status: "planned", date: "Feb 2026" },
    { feature: "SMS Reminders (Sinch)", status: "planned", date: "Feb 2026" },
    { feature: "Acuity Integration", status: "planned", date: "Feb 2026" },
    { feature: "Quick Book Again", status: "planned", date: "Feb 2026" },
    { feature: "Portfolio Import", status: "planned", date: "Mar 2026" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <PhygitalIcon className="w-8 h-8" />
              <span className="font-display text-xl font-bold">phygital</span>
            </Link>
            <Badge variant="outline" className="text-xs">
              Status Page
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Overall Status */}
        <div className={`rounded-2xl p-6 mb-8 ${allOperational ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-3">
            {allOperational ? (
              <CheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {allOperational ? "All Systems Operational" : "Partial System Degradation"}
              </h1>
              <p className="text-sm text-gray-700">
                Last checked: {lastChecked ? lastChecked.toLocaleTimeString() : "Checking..."}
              </p>
            </div>
            <button 
              onClick={checkStatus}
              disabled={loading}
              className="ml-auto p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Service Status */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Service Status</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {/* API */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Server className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">API Server</p>
                  <p className="text-sm text-gray-600">Backend services</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {status.api.latency && (
                  <span className="text-sm text-gray-600">{status.api.latency}ms</span>
                )}
                <Badge className={getStatusColor(status.api.status)}>
                  {status.api.status}
                </Badge>
                {getStatusIcon(status.api.status)}
              </div>
            </div>

            {/* Database */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Database</p>
                  <p className="text-sm text-gray-600">MongoDB</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={getStatusColor(status.database.status)}>
                  {status.database.status}
                </Badge>
                {getStatusIcon(status.database.status)}
              </div>
            </div>

            {/* Frontend */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Web App</p>
                  <p className="text-sm text-gray-600">Frontend</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={getStatusColor(status.frontend.status)}>
                  {status.frontend.status}
                </Badge>
                {getStatusIcon(status.frontend.status)}
              </div>
            </div>
          </div>
        </section>

        {/* Development Roadmap */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Development Roadmap</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {roadmap.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    item.status === 'complete' ? 'bg-green-100' :
                    item.status === 'in-progress' ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    {item.status === 'complete' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : item.status === 'in-progress' ? (
                      <Zap className="w-4 h-4 text-amber-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <span className="font-medium text-sm">{item.feature}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-600">{item.date}</span>
                  <Badge variant="outline" className={`text-xs ${
                    item.status === 'complete' ? 'border-green-300 text-green-700' :
                    item.status === 'in-progress' ? 'border-amber-300 text-amber-700' : ''
                  }`}>
                    {item.status === 'in-progress' ? 'In Progress' : 
                     item.status === 'complete' ? 'Complete' : 'Planned'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Mocked Features */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Mocked Features</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800 mb-3">
              The following features have UI built but use mocked data:
            </p>
            <ul className="space-y-2 text-sm text-amber-700">
              <li className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Acuity Scheduler integration
              </li>
              <li className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                SMS reminders (marked sent, no actual SMS)
              </li>
              <li className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Pinterest inspiration import
              </li>
            </ul>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-600 text-sm">
        <Link to="/" className="hover:text-gray-900">← Back to Phygital</Link>
      </footer>
    </div>
  );
};

export default StatusPage;
